import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "./contexts/authContext";
import { useOptroBulkImportEnabled } from "./hooks/useOptroBulkImportEnabled";
import { useOptroIntegrationsHidden } from "./hooks/useOptroIntegrationsHidden";
import { useWorkStepsEnabled } from "./hooks/useWorkStepsEnabled";
import { useFeatureFlagsReady } from "./contexts/launchDarklyContext";
import { Loader2 } from "lucide-react";
import { Component, lazy, Suspense, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import {
  refetchAccessToken,
  useCheckTokenAndFetchUser,
} from "./hooks/useCheckTokenAndFetchUser";
import { LoadingSpinner } from "./components/ui/loadingSpinner/loadingSpinner";
import { AppUpdateNotification } from "./components/AppUpdateNotification";
import { isChunkLoadError } from "./lib/staleChunkReload";
import { ForethoughtWidget } from "./components/ForethoughtWidget";
import { MetricsPageviews } from "./components/MetricsPageviews";
import { getExtractionRoutes } from "./routes/ExtractionRoutes";
import { RequireReportExtractions } from "./routes/RequireReportExtractions";
import { AuditRoutesV2 } from "./routes/AuditRoutes.tsx";
import { WorkStepAuditRoutesV2 } from "./routes/WorkStepAuditRoutes.tsx";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { SignIn } from "./pages/SignIn";
import { SidebarContainer } from "./components/sidebar/sidebarContainer";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { SSOCallback } from "./pages/auth/SSOCallback";
import { OptroCallback } from "./pages/auth/OptroCallback";
import { ReferralSignIn } from "./pages/ReferralSignIn.tsx";
import { InviteToWorkspaceSignup } from "./pages/extraction/workspaceMembers/InviteToWorkspaceSignup.tsx";
import { SubscriptionInactivePage } from "./pages/auth/SubscriptionInactivePage.tsx";
import { PartnerTermsPage } from "./pages/auth/PartnerTermsPage.tsx";
import { Terms } from "./pages/Terms.tsx";
import { AuditProvider } from "./contexts/auditContext.tsx";
import { FieldGroupsProvider } from "./contexts/fieldGroupsContext.tsx";
import { HookCacheProvider } from "./lib/hookCache";
import CreateControlPageV2 from "./pages/audit/controls/CreateControlPageV2.tsx";
import CreateControlChooserPage from "./pages/audit/controls/CreateControlChooserPage.tsx";
import { CollabControlPage } from "./pages/audit/controls/CollabControlPage.tsx";
import { WorkPaperTemplatesPage } from "./pages/workPaper/WorkPaperTemplatesPage.tsx";
import { WorkPaperTemplateEditorPage } from "./pages/workPaper/WorkPaperTemplateEditorPage.tsx";
import { WorkspaceDocumentsPage } from "./pages/workspaceDocuments/WorkspaceDocumentsPage.tsx";
import { RcmLandingPage } from "./pages/audit/rcmImport/RcmLandingPage.tsx";
import { UploadRcmPage } from "./pages/audit/rcmImport/UploadRcmPage.tsx";
import { MapRcmColumnsPage } from "./pages/audit/rcmImport/MapRcmColumnsPage.tsx";
import { RcmImportProvider } from "./contexts/rcmImportContext.tsx";
import { ControlsPageV2 } from "./pages/audit/controls/ControlsPageV2.tsx";
import { FieldworkUnifiedPage } from "./pages/audit/fieldwork/FieldworkUnifiedPage.tsx";
import { WorkStepsPage } from "./pages/audit/workSteps/WorkStepsPage.tsx";
import { CreateWorkStepChooserPage } from "./pages/audit/workSteps/CreateWorkStepChooserPage.tsx";
import { OptroWorkStepImportWizardLayout } from "./pages/audit/workSteps/optroImport/OptroWorkStepImportWizardLayout.tsx";
import { OptroWorkStepImportSelectAuditsPage } from "./pages/audit/workSteps/optroImport/OptroWorkStepImportSelectAuditsPage.tsx";
import { OptroWorkStepImportSelectPage } from "./pages/audit/workSteps/optroImport/OptroWorkStepImportSelectPage.tsx";
import { OptroWorkStepImportReviewPage } from "./pages/audit/workSteps/optroImport/OptroWorkStepImportReviewPage.tsx";
import { CreateWorkStepPage } from "./pages/audit/workSteps/CreateWorkStepPage.tsx";
import { CollabWorkStepPage } from "./pages/audit/workSteps/CollabWorkStepPage.tsx";
import { UploadWorkStepsPage } from "./pages/audit/workSteps/import/UploadWorkStepsPage.tsx";
import { ReviewWorkStepImportPage } from "./pages/audit/workSteps/import/ReviewWorkStepImportPage.tsx";
import { WorkStepImportProvider } from "./contexts/workStepImportContext.tsx";
import { OptroImportWizardLayout } from "./pages/audit/controls/optroImport/OptroImportWizardLayout.tsx";
import { OptroImportSelectPage } from "./pages/audit/controls/optroImport/OptroImportSelectPage.tsx";
import { OptroImportReviewPage } from "./pages/audit/controls/optroImport/OptroImportReviewPage.tsx";
import { OptroImportProvider } from "./contexts/optroImportContext.tsx";
import { OptroSendProvider } from "./contexts/optroSendContext.tsx";
import { OptroImportProgressStack } from "./components/OptroImportProgressStack.tsx";
import { WorkStepOptroImportProvider } from "./contexts/workStepOptroImportContext.tsx";
import {
  LibraryListPage,
  CreateLibraryTypePage,
} from "./pages/audit/libraries";
import { ReportExtractionsPage } from "./pages/reportExtraction/ReportExtractionsPage.tsx";
import { NewReportExtractionPage } from "./pages/reportExtraction/NewReportExtractionPage.tsx";
import { ViewReportExtractionPage } from "./pages/reportExtraction/ViewReportExtractionPage.tsx";
import { ApprovalsPage } from "./pages/audit/approvals/ApprovalsPage.tsx";
import { OptroLaunchPage } from "./pages/audit/optroLaunch/OptroLaunchPage.tsx";
import { OptroLaunchFailedPage } from "./pages/audit/optroLaunch/OptroLaunchFailedPage.tsx";
import { RequireOptroViewAccess } from "./components/audit/optroAccess/RequireOptroViewAccess.tsx";
import { ZendeskRedirect } from "./pages/support/ZendeskRedirect";
import { config } from "@/config";
import { stashPostLoginRedirect } from "./lib/authRedirect";
import { isPartnerTermsGated } from "./lib/partnerTerms";

// Only lazy load the heavy/rarely accessed pages
const SettingsPage = lazy(() =>
  import("./pages/admin/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const WorkspaceMembersPage = lazy(() =>
  import("./pages/extraction/workspaceMembers/WorkspaceMembersPage").then(
    (module) => ({ default: module.WorkspaceMembersPage }),
  ),
);
const ControlConfigurationPageV2 = lazy(() =>
  import("./pages/audit/controls/ControlConfigurationPageV2").then(
    (module) => ({ default: module.ControlConfigurationPageV2 }),
  ),
);
const WorkStepConfigurationPage = lazy(() =>
  import("./pages/audit/workSteps/WorkStepConfigurationPage").then(
    (module) => ({ default: module.WorkStepConfigurationPage }),
  ),
);
const IntegrationsPage = lazy(() =>
  import("./pages/admin/integrations/IntegrationsPage").then((module) => ({
    default: module.IntegrationsPage,
  })),
);
const IntegrationDetailPage = lazy(() =>
  import("./pages/admin/integrations/IntegrationDetailPage").then((module) => ({
    default: module.IntegrationDetailPage,
  })),
);
const RolesPage = lazy(() =>
  import("./pages/admin/roles/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);
const AuditEventsPage = lazy(() =>
  import("./pages/audit/auditEvents/AuditEventsPage").then((module) => ({
    default: module.AuditEventsPage,
  })),
);
// Dev-only fixture gallery for audit log event rendering; never routed in
// production builds.
const AuditEventGallery = lazy(() =>
  import("./pages/dev/AuditEventGallery").then((module) => ({
    default: module.AuditEventGallery,
  })),
);

// Error boundary that shows an update prompt on chunk load failures caused by
// stale deployment (the old chunk hash no longer exists on the CDN).
class ChunkErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    if (isChunkLoadError(error)) {
      return { hasError: true };
    }
    throw error;
  }

  render() {
    if (this.state.hasError) {
      return <AppUpdateNotification />;
    }
    return this.props.children;
  }
}

// Wrapper component with Suspense
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <ChunkErrorBoundary>
    <Suspense
      fallback={
        <div className="h-screen flex justify-center items-center">
          <LoadingSpinner />
        </div>
      }
    >
      {children}
    </Suspense>
  </ChunkErrorBoundary>
);

// Close code the backend sends when websocket token verification fails
// (see MS-2497); treated as "refresh the token before reconnecting".
const WS_AUTH_FAILURE_CLOSE_CODE = 4401;

// WebSocket connection management
function useWebSocketConnection() {
  useEffect(() => {
    const wsEndpoint = config.apiUrl.replace(/^http/, "ws") + "/notifications/";

    let socket: WebSocket | null = null;
    let isIntentionalClose = false;
    let errorToastShown = false;
    let isAuthenticated = false;
    let reconnectAttempts = 0;
    let forceTokenRefresh = false;
    let reconnectTimer: number | null = null;
    // Guards the async gap in connectWebSocket (the token refresh await) so
    // a refocus or timer cannot start a second connect while one is already
    // in flight. Once `socket` is assigned, readyState checks take over.
    let isConnecting = false;

    // Returns an access token that is safe to send, refreshing it first when
    // it is expired (or about to expire), malformed, or was rejected by the
    // server on the previous attempt. Returns null when no usable token can
    // be obtained — a failed refresh has already cleared storage and
    // redirected to /signin, so the caller must not connect or retry.
    const ensureFreshToken = async (): Promise<string | null> => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("Cannot authenticate: missing token");
        return null;
      }

      let needsRefresh = forceTokenRefresh;
      if (!needsRefresh) {
        try {
          const { exp } = jwtDecode(token);
          // Refresh when exp is absent or within 30 seconds of expiry.
          needsRefresh = !exp || exp - Date.now() / 1000 < 30;
        } catch {
          // A token that cannot be decoded also needs a refresh.
          needsRefresh = true;
        }
      }

      if (!needsRefresh) {
        return token;
      }

      console.log("Refreshing access token before WebSocket connect");
      const refreshed = await refetchAccessToken();
      if (!refreshed) {
        return null;
      }
      forceTokenRefresh = false;
      return localStorage.getItem("access_token");
    };

    const connectWebSocket = async () => {
      if (isConnecting) {
        return;
      }
      isConnecting = true;
      console.log("Connecting to WebSocket...");

      // Get authentication tokens, same as fetchFastApi — but never connect
      // with a token that is locally known to be expired (MS-2497: expired
      // tokens caused an infinite reconnect loop).
      const token = await ensureFreshToken();
      if (!token) {
        isConnecting = false;
        return;
      }
      // The hook may have been cleaned up (unmount/unload) while the refresh
      // was in flight — never construct a zombie socket in that case.
      if (isIntentionalClose) {
        isConnecting = false;
        return;
      }
      const workspaceId = localStorage.getItem("workspace_id");

      // Create WebSocket connection without authentication in URL
      socket = new WebSocket(wsEndpoint);
      isConnecting = false;

      // Connection opened
      socket.addEventListener("open", (event) => {
        console.log("WebSocket connection established");
        errorToastShown = false; // Reset error flag on successful connection

        // Use event.currentTarget to avoid a race condition where the outer
        // `socket` variable is reassigned to a new CONNECTING socket before
        // this handler fires (e.g. during rapid reconnection).
        const ws = event.currentTarget;
        if (ws instanceof WebSocket) {
          ws.send(
            JSON.stringify({
              token: token,
              workspace_id: workspaceId || "",
            }),
          );
        }
      });

      // Listen for messages
      socket.addEventListener("message", (event) => {
        try {
          const data = JSON.parse(event.data);

          // Check for authentication acknowledgment
          if (data.status === "authenticated") {
            console.log("WebSocket authentication successful");
            isAuthenticated = true;
            reconnectAttempts = 0; // Reset backoff after a successful auth
            return;
          }

          // Only process regular messages if authenticated
          if (!isAuthenticated) {
            console.warn(
              "Received message before authentication was confirmed",
            );
            return;
          }

          // Display toast with message content
          // Additional handling based on message type
          if (data.type === "error") {
            const errorDescription = data.error ? `${data.error}` : "";
            toast.error(data.message || "An error occurred", {
              description: errorDescription,
              duration: 8000,
            });
          } else if (data.type === "success") {
            toast.success(data.message || "Operation completed successfully", {
              duration: 5000,
            });
          } else {
            toast.info("New message received", {
              description: data.message || "A new notification arrived",
              duration: 5000,
            });
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      });

      // Handle errors
      socket.addEventListener("error", (event) => {
        console.error("WebSocket error:", event);
        // Only show the error toast once and not during intentional close
        if (!isIntentionalClose && !errorToastShown) {
          errorToastShown = true;
          toast.error("Connection to notification service failed", {
            duration: 3000,
          });
        }
      });

      // Handle connection closed
      socket.addEventListener("close", (event) => {
        console.log("WebSocket connection closed", event);
        isAuthenticated = false; // Reset authentication status on disconnect

        // The server rejected the token: force a refresh before the next
        // attempt even if the token still looks valid locally (covers clock
        // skew and server-side revocation).
        if (event.code === WS_AUTH_FAILURE_CLOSE_CODE) {
          forceTokenRefresh = true;
        }

        // Don't try to reconnect if this was intentional or during page unload
        if (isIntentionalClose) {
          return;
        }

        // Attempt to reconnect with exponential backoff (3s base, 60s cap)
        const delay = Math.min(3000 * 2 ** reconnectAttempts, 60000);
        reconnectAttempts += 1;
        if (reconnectTimer !== null) {
          window.clearTimeout(reconnectTimer);
        }
        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;
          if (document.visibilityState !== "hidden") {
            connectWebSocket();
          }
        }, delay);
      });
    };

    // Start connection
    connectWebSocket();

    // Handle visibility change to reconnect when tab becomes visible again
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        !isConnecting &&
        (!socket || socket.readyState === WebSocket.CLOSED)
      ) {
        // Cancel any pending scheduled retry so the refocus connect and the
        // backoff timer cannot race into a double-connect.
        if (reconnectTimer !== null) {
          window.clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        connectWebSocket();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Handle page before unload - mark close as intentional
    const handleBeforeUnload = () => {
      isIntentionalClose = true;
      if (socket) {
        socket.close();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup on unmount
    return () => {
      isIntentionalClose = true; // Mark as intentional when component unmounts
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (socket) {
        socket.close();
      }
    };
  }, []);
}

// Sentry route instrumentation for the data router (replaces the component
// router's withSentryReactRouterV7Routing). Only instrument in production.
const createInstrumentedRouter = import.meta.env.PROD
  ? Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)
  : createBrowserRouter;

// Root layout for the data router. With RouterProvider there are no arbitrary
// siblings inside the router, so the app-wide chrome (the support widget) lives
// here above the matched route's <Outlet />.
function RootLayout() {
  const { user } = useAuth();
  return (
    <>
      <MetricsPageviews />
      {user && <ForethoughtWidget />}
      {/* The Optro import trackers sit above every route so imports keep
          polling, and the sidebar's Background Tasks keep showing them, when
          the user leaves the /audit layout (the /dashboard and session
          layouts render a sidebar too). They only need auth and feature
          flags, which are above the router; nothing is polled while signed
          out or without the import permission. The progress cards mount here
          too, so opening an import from Background Tasks shows its card on
          every route; they render nothing while the trackers have no job
          (signed out, /accept-terms, non-audit workspaces, work steps off). */}
      <OptroImportProvider>
        <WorkStepOptroImportProvider>
          <Outlet />
          <OptroImportProgressStack />
        </WorkStepOptroImportProvider>
      </OptroImportProvider>
    </>
  );
}

// Bounce an unauthenticated visitor to sign-in, capturing where they were
// headed so it can be restored after login. The stash runs in a mount effect
// (a real side effect) rather than during render.
function RedirectToSignIn() {
  const location = useLocation();
  useEffect(() => {
    stashPostLoginRedirect(location.pathname + location.search + location.hash);
  }, [location]);
  return <Navigate to="/signin" replace />;
}

// Shared by RequireAuth and RequireAuthAndAuditEnabled: the Partner-terms
// gate redirect when the current workspace is Partner and the member hasn't
// accepted terms yet, else null. One place so the two guards can't drift on
// when the gate applies (RequireAuthAndAuditEnabled guards /audit, the
// default post-login landing target, so it needs this exactly like
// RequireAuth does). Never applied to the /accept-terms route itself, which
// uses RequireAuthNoTermsGate instead.
function partnerTermsGateRedirect(
  auth: ReturnType<typeof useAuth>,
  location: ReturnType<typeof useLocation>,
): JSX.Element | null {
  if (isPartnerTermsGated(auth.workspace, auth.workspaceMember)) {
    return <Navigate to="/accept-terms" state={{ from: location }} replace />;
  }
  return null;
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.loading && !auth.workspace && auth.user) {
      auth.logout();
      toast.error(
        "Workspace not found, please make sure you are a member of a workspace",
        {
          duration: 10000,
        },
      );
      navigate("/signin", { state: { from: location }, replace: true });
    }
  }, [auth, location, navigate]);

  if (auth.loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!auth.user) {
    return <RedirectToSignIn />;
  }

  if (auth.workspace?.subscriptionStatus === "Inactive") {
    return (
      <Navigate
        to="/subscription-inactive"
        state={{ from: location }}
        replace
      />
    );
  }

  const termsGateRedirect = partnerTermsGateRedirect(auth, location);
  if (termsGateRedirect) {
    return termsGateRedirect;
  }

  return children;
}

// Guards the /accept-terms route itself: needs a signed-in user (so the terms
// page can read workspace/workspaceMember and POST accept-terms) but must NOT
// re-apply the Partner-terms check RequireAuth does, otherwise a gated user
// would be bounced right back to this same route in an infinite redirect
// loop. Mirrors RequireAuth minus its final terms check.
function RequireAuthNoTermsGate({ children }: { children: JSX.Element }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.loading && !auth.workspace && auth.user) {
      auth.logout();
      toast.error(
        "Workspace not found, please make sure you are a member of a workspace",
        {
          duration: 10000,
        },
      );
      navigate("/signin", { state: { from: location }, replace: true });
    }
  }, [auth, location, navigate]);

  if (auth.loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!auth.user) {
    return <RedirectToSignIn />;
  }

  if (auth.workspace?.subscriptionStatus === "Inactive") {
    return (
      <Navigate
        to="/subscription-inactive"
        state={{ from: location }}
        replace
      />
    );
  }

  return children;
}

// Each guard reads its flag in the same component as useFeatureFlagsReady: a
// flag passed in as a prop can still hold its pre-login value when ready flips.

// Keeps deep links out of the Optro import wizard while optro-bulk-import is off.
function RequireOptroBulkImport({ children }: { children: JSX.Element }) {
  const flagsReady = useFeatureFlagsReady();
  const enabled = useOptroBulkImportEnabled();
  if (!flagsReady) {
    return (
      <div className="h-screen flex justify-center items-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }
  if (!enabled) {
    return <Navigate to="/audit/controls/v2" replace />;
  }
  return children;
}

// Keeps deep links out of every work-steps route while the work-steps flag is off.
function RequireWorkSteps({ children }: { children: JSX.Element }) {
  const flagsReady = useFeatureFlagsReady();
  const enabled = useWorkStepsEnabled();
  if (!flagsReady) {
    return (
      <div className="h-screen flex justify-center items-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }
  if (!enabled) {
    return <Navigate to="/audit/controls/v2" replace />;
  }
  return children;
}

// Hides the legacy AuditBoard "Integrations" admin page once the workspace is on
// Optro sign-in (its instance/credential come from the OAuth token, not that
// page). Gated on flag AND provider==optro via useOptroIntegrationsHidden, and
// held until flags are authoritative: the hook reads hidden=false on a cold load,
// so without this flagsReady hold an Optro session would render the page and then
// get redirected away once the flag resolved (a visible flash).
function RequireIntegrationsVisible({ children }: { children: JSX.Element }) {
  const flagsReady = useFeatureFlagsReady();
  const hidden = useOptroIntegrationsHidden();
  if (!flagsReady) {
    return (
      <div className="h-screen flex justify-center items-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }
  if (hidden) {
    return <Navigate to="/admin/workspace_members" replace />;
  }
  return children;
}

function RequireAuthAndAuditEnabled({ children }: { children: JSX.Element }) {
  const auth = useAuth();
  const location = useLocation();
  // Initialize WebSocket connection
  useWebSocketConnection();

  if (auth.loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!auth.user) {
    return <RedirectToSignIn />;
  }

  if (!auth.workspace?.auditEnabled) {
    return <Navigate to="/dashboard/extraction/new" replace />;
  }

  if (auth.workspace?.subscriptionStatus === "Inactive") {
    return (
      <Navigate
        to="/subscription-inactive"
        state={{ from: location }}
        replace
      />
    );
  }

  const termsGateRedirect = partnerTermsGateRedirect(auth, location);
  if (termsGateRedirect) {
    return termsGateRedirect;
  }

  return children;
}

function AuthenticatedRedirect({ children }: { children: JSX.Element }) {
  const auth = useAuth();

  if (auth.loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (auth.user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function App() {
  const {
    user,
    loading,
    accessToken,
    workspace,
    workspaces,
    workspaceMember,
    analyticsDestination,
    analyticsIdentity,
  } = useCheckTokenAndFetchUser();
  const { setAuth } = useAuth();
  useEffect(() => {
    if (loading) {
      return;
    }

    if (user && workspace && workspaceMember) {
      console.log("Setting auth to user and workspace", user, workspace);
      setAuth({
        user,
        loading: false,
        accessToken,
        workspace,
        workspaceMember,
        workspaces,
        analyticsDestination,
        analyticsIdentity,
      });
    } else if (user) {
      console.log("Setting auth to user", user);
      setAuth({
        user,
        loading: false,
        accessToken,
        workspace: null,
        workspaceMember: null,
        workspaces,
        analyticsDestination,
        analyticsIdentity,
      });
    } else {
      console.log("Setting auth to null", user);
      setAuth({
        user: null,
        loading: false,
        accessToken: null,
        workspace: null,
        workspaceMember: null,
        workspaces: [],
        analyticsDestination: null,
        analyticsIdentity: null,
      });
    }
  }, [
    user,
    setAuth,
    loading,
    accessToken,
    workspace,
    workspaces,
    workspaceMember,
    analyticsDestination,
    analyticsIdentity,
  ]);

  if (loading && window.location.pathname !== "/website-playground") {
    return (
      <div className="h-screen flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );
  }

  return <AppRouter />;
}

// Builds the data router exactly once: RouterProvider requires a stable router
// instance for its lifetime, so the route tree is captured in a lazy useState
// initializer rather than a memo whose identity could otherwise change and
// desync routing. Consequence: NOTHING in this tree may close over a feature
// flag, entitlement or other auth-derived value — the initializer runs once and
// never re-reads its inputs, and on the SSO/Optro callback path it runs before
// auth has been restored. Gate on such values inside a guard component that
// reads them reactively (see RequireReportExtractions).
function AppRouter() {
  const [router] = useState(() =>
    createInstrumentedRouter(
      createRoutesFromElements(
        <Route element={<RootLayout />}>
          <Route
            path="/signin"
            element={
              <AuthenticatedRedirect>
                <SignIn />
              </AuthenticatedRedirect>
            }
          />
          <Route
            path="/referral"
            element={
              <AuthenticatedRedirect>
                <ReferralSignIn />
              </AuthenticatedRedirect>
            }
          />
          <Route
            path="/invite/:inviteCode"
            element={<InviteToWorkspaceSignup />}
          />
          <Route
            path="/reset-password"
            element={
              <AuthenticatedRedirect>
                <ResetPassword />
              </AuthenticatedRedirect>
            }
          />
          <Route path="/sso/callback" element={<SSOCallback />} />
          <Route path="/auth/optrooauth/callback" element={<OptroCallback />} />
          <Route
            path="/subscription-inactive"
            element={<SubscriptionInactivePage />}
          />
          <Route
            path="/accept-terms"
            element={
              <RequireAuthNoTermsGate>
                <PartnerTermsPage />
              </RequireAuthNoTermsGate>
            }
          />
          <Route path="/terms" element={<Terms />} />
          {import.meta.env.DEV && (
            <Route path="/dev/audit-gallery" element={<AuditEventGallery />} />
          )}
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <FieldGroupsProvider>
                  <SidebarContainer />
                </FieldGroupsProvider>
              </RequireAuth>
            }
          >
            {getExtractionRoutes()}
          </Route>
          <Route
            path="/audit/"
            element={
              <RequireAuthAndAuditEnabled>
                {/* `useHookCacheQuery` reads this context before it honours
                    its `enabled` argument, so a hook that is switched off by
                    a feature flag still needs the provider to exist. The
                    control-configuration page — through the work-paper column
                    dialogs — is the only route under this layout that renders
                    an analytics hook today; the provider sits here rather than
                    on that page because the cache lives only as long as the
                    provider is mounted, so a per-page provider would drop it
                    on every navigation. */}
                <HookCacheProvider>
                  <FieldGroupsProvider>
                    {/* RCM import state lives at the audit level: the upload
                        step can start inline on the create-control page and
                        continue at /audit/create-control/map, so both routes
                        must share one provider instance. */}
                    <RcmImportProvider>
                      {/* Work Step spreadsheet import spans upload + review
                          routes, so its state also lives at the audit level.
                          (The Optro import trackers live in RootLayout.) */}
                      <WorkStepImportProvider>
                        <SidebarContainer />
                      </WorkStepImportProvider>
                    </RcmImportProvider>
                  </FieldGroupsProvider>
                </HookCacheProvider>
              </RequireAuthAndAuditEnabled>
            }
          >
            {/* Static redirect to the (only) controls page. This MUST NOT be a
                router-creation-time feature-flag closure: AppRouter builds the
                router once in a useState initializer, and on the Optro/SSO
                callback the workspace hasn't loaded yet, so a captured
                controlsV2Enabled=false froze the index at the nonexistent
                /audit/controls, which fell through to the "*" wildcard
                (-> /audit) and looped forever, remounting the whole /audit
                layout. There is no v1 /audit/controls route. */}
            <Route
              index
              element={<Navigate to="/audit/controls/v2" replace />}
            />
            <Route path="create-control">
              <Route index element={<RcmLandingPage />} />
              <Route path="upload" element={<UploadRcmPage />} />
              <Route path="map" element={<MapRcmColumnsPage />} />
            </Route>
            <Route path="controls/v2" element={<ControlsPageV2 />} />
            {/* Fieldwork: the unified controls + work steps testing list.
                Behind RequireWorkSteps because the surface only exists as a
                merged view where work steps exist, and /fieldwork/with-sessions
                carries require_work_steps on the backend for the same reason.
                The controls-only Fieldwork tab on ControlsPageV2 remains the
                flag-off path. Top-level /audit/fieldwork segment for the same
                reason work-steps is one: SidebarLink isActive uses substring
                matching. */}
            <Route
              path="fieldwork"
              element={
                <RequireWorkSteps>
                  <FieldworkUnifiedPage />
                </RequireWorkSteps>
              }
            />
            {/* Work Steps landing page. Top-level /audit/work-steps segment —
                SidebarLink isActive uses substring matching, so nesting under
                controls would light both links. Every work-steps route
                element below is wrapped in RequireWorkSteps so a deep link
                can't reach the surface while the work-steps flag is off. */}
            <Route
              path="work-steps"
              element={
                <RequireWorkSteps>
                  <WorkStepsPage />
                </RequireWorkSteps>
              }
            />
            {/* Work Step creation flows */}
            <Route
              path="work-steps/create"
              element={
                <RequireWorkSteps>
                  <CreateWorkStepChooserPage />
                </RequireWorkSteps>
              }
            />
            <Route
              path="work-steps/create/manual"
              element={
                <RequireWorkSteps>
                  <CreateWorkStepPage />
                </RequireWorkSteps>
              }
            />
            {/* Revise with AI — same wizard, seeded from an existing work step
                via ?mode=refine&id=<workStepId>. Static segment must precede
                the :sessionId route (mirrors controls/v2/collab/refine). */}
            <Route
              path="work-steps/collab/refine"
              element={
                <RequireWorkSteps>
                  <RequireOptroViewAccess kind="work-step" idSearchParam="id">
                    <CollabWorkStepPage />
                  </RequireOptroViewAccess>
                </RequireWorkSteps>
              }
            />
            <Route
              path="work-steps/collab/:sessionId"
              element={
                <RequireWorkSteps>
                  <RequireOptroViewAccess
                    kind="collab-work-step-session"
                    idParam="sessionId"
                  >
                    <CollabWorkStepPage />
                  </RequireOptroViewAccess>
                </RequireWorkSteps>
              }
            />
            {/* Work Step Optro import wizard */}
            <Route
              path="work-steps/import/optro"
              element={
                <RequireWorkSteps>
                  <OptroWorkStepImportWizardLayout />
                </RequireWorkSteps>
              }
            >
              <Route index element={<OptroWorkStepImportSelectAuditsPage />} />
              <Route
                path="work-steps"
                element={<OptroWorkStepImportSelectPage />}
              />
              <Route
                path="review"
                element={<OptroWorkStepImportReviewPage />}
              />
            </Route>
            {/* Work Step spreadsheet bulk import */}
            <Route
              path="work-steps/import"
              element={
                <RequireWorkSteps>
                  <UploadWorkStepsPage />
                </RequireWorkSteps>
              }
            />
            <Route
              path="work-steps/import/review"
              element={
                <RequireWorkSteps>
                  <ReviewWorkStepImportPage />
                </RequireWorkSteps>
              }
            />
            {/* Work Step configuration page. Static segments above must
                precede this :id route. */}
            <Route
              path="work-steps/:id"
              element={
                <RequireWorkSteps>
                  <RequireOptroViewAccess kind="work-step" idParam="id">
                    <SuspenseWrapper>
                      <WorkStepConfigurationPage />
                    </SuspenseWrapper>
                  </RequireOptroViewAccess>
                </RequireWorkSteps>
              }
            />
            <Route
              path="optro/launch-failed"
              element={
                <SuspenseWrapper>
                  <OptroLaunchFailedPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path="controls/v2/create"
              element={<CreateControlChooserPage />}
            />
            <Route
              path="controls/v2/create/manual"
              element={<CreateControlPageV2 />}
            />
            {/* Revise with AI — same wizard, seeded from an existing control
                via ?mode=refine&id=<controlId>. Static segment must precede
                the :sessionId route. */}
            <Route
              path="controls/v2/collab/refine"
              element={
                <RequireOptroViewAccess kind="control" idSearchParam="id">
                  <CollabControlPage />
                </RequireOptroViewAccess>
              }
            />
            <Route
              path="controls/v2/import/optro"
              element={
                <RequireOptroBulkImport>
                  <OptroImportWizardLayout />
                </RequireOptroBulkImport>
              }
            >
              <Route index element={<OptroImportSelectPage />} />
              <Route path="review" element={<OptroImportReviewPage />} />
            </Route>
            <Route
              path="controls/v2/collab/:sessionId"
              element={
                <RequireOptroViewAccess
                  kind="collab-control-session"
                  idParam="sessionId"
                >
                  <CollabControlPage />
                </RequireOptroViewAccess>
              }
            />
            {/* Pathless guard: the config page and its builder share one
                access check, so moving between them doesn't re-check. */}
            <Route
              element={<RequireOptroViewAccess kind="control" idParam="id" />}
            >
              <Route
                path="controls/v2/:id"
                element={
                  <SuspenseWrapper>
                    <ControlConfigurationPageV2 />
                  </SuspenseWrapper>
                }
              />
              <Route
                path="controls/v2/:id/builder"
                element={
                  <SuspenseWrapper>
                    <ControlConfigurationPageV2 />
                  </SuspenseWrapper>
                }
              />
            </Route>
            <Route
              path="work-paper-templates"
              element={<WorkPaperTemplatesPage />}
            />
            <Route
              path="work-paper-templates/new"
              element={<WorkPaperTemplateEditorPage />}
            />
            <Route
              path="work-paper-templates/:templateId/edit"
              element={<WorkPaperTemplateEditorPage />}
            />
            <Route path="documents" element={<WorkspaceDocumentsPage />} />
            <Route path="libraries/new" element={<CreateLibraryTypePage />} />
            <Route path="libraries/:groupId" element={<LibraryListPage />} />
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route
              path="audit-logs"
              element={
                <SuspenseWrapper>
                  <AuditEventsPage />
                </SuspenseWrapper>
              }
            />
            {/* Pathless guard route: the child paths are unchanged, and the
                flag is read reactively inside the guard instead of being
                captured by the router initializer above. */}
            <Route element={<RequireReportExtractions redirectTo="/audit" />}>
              <Route
                path="report-extraction/jobs"
                element={<ReportExtractionsPage />}
              />
              <Route
                path="report-extraction/new"
                element={<NewReportExtractionPage />}
              />
              <Route
                path="report-extraction/view/:jobId"
                element={<ViewReportExtractionPage />}
              />
            </Route>
          </Route>

          {/* The access guard sits inside the app shell, so the sidebar stays
              up while it checks and on its error page, and outside the
              session's providers, so the session's data (AuditProvider) is
              not fetched until access is confirmed. The sidebar needs only
              the workspace-level providers above it. The guard reads the
              child routes' :controlSessionId — useParams in a layout sees
              its matched children's params. */}
          <Route
            path="/audit/control-sessions/session"
            element={
              <RequireAuthAndAuditEnabled>
                <HookCacheProvider>
                  <FieldGroupsProvider>
                    <SidebarContainer />
                  </FieldGroupsProvider>
                </HookCacheProvider>
              </RequireAuthAndAuditEnabled>
            }
          >
            <Route
              element={
                <RequireOptroViewAccess
                  kind="control-session"
                  idParam="controlSessionId"
                >
                  <AuditProvider>
                    <OptroSendProvider>
                      <Outlet />
                    </OptroSendProvider>
                  </AuditProvider>
                </RequireOptroViewAccess>
              }
            >
              {AuditRoutesV2}
            </Route>
          </Route>

          {/* Work-step sibling of the control-session shell above: same
              wrapper composition plus the work-steps flag gate, with the
              provider pointed at the /work-steps session endpoints. */}
          <Route
            path="/audit/work-step-sessions/session"
            element={
              <RequireAuthAndAuditEnabled>
                <RequireWorkSteps>
                  <HookCacheProvider>
                    <FieldGroupsProvider>
                      <SidebarContainer />
                    </FieldGroupsProvider>
                  </HookCacheProvider>
                </RequireWorkSteps>
              </RequireAuthAndAuditEnabled>
            }
          >
            <Route
              element={
                <RequireOptroViewAccess
                  kind="work-step-session"
                  idParam="controlSessionId"
                >
                  <AuditProvider sessionKind="work_step">
                    <OptroSendProvider>
                      <Outlet />
                    </OptroSendProvider>
                  </AuditProvider>
                </RequireOptroViewAccess>
              }
            >
              {WorkStepAuditRoutesV2}
            </Route>
          </Route>

          <Route
            path="/admin"
            element={
              <RequireAuth>
                <FieldGroupsProvider>
                  <SidebarContainer />
                </FieldGroupsProvider>
              </RequireAuth>
            }
          >
            <Route
              path="workspace_members"
              element={
                <SuspenseWrapper>
                  <WorkspaceMembersPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path="integrations"
              element={
                <RequireIntegrationsVisible>
                  <SuspenseWrapper>
                    <IntegrationsPage />
                  </SuspenseWrapper>
                </RequireIntegrationsVisible>
              }
            />
            <Route
              path="integrations/:systemId"
              element={
                <RequireIntegrationsVisible>
                  <SuspenseWrapper>
                    <IntegrationDetailPage />
                  </SuspenseWrapper>
                </RequireIntegrationsVisible>
              }
            />
            <Route
              path="roles"
              element={
                <SuspenseWrapper>
                  <RolesPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path="settings"
              element={
                <SuspenseWrapper>
                  <SettingsPage />
                </SuspenseWrapper>
              }
            />
          </Route>
          <Route
            path="/support/zendesk"
            element={
              <RequireAuth>
                <ZendeskRedirect />
              </RequireAuth>
            }
          />
          {/* Deliberately NOT nested under /audit/ — RequireAuthAndAuditEnabled
              checks the ACTIVE workspace's auditEnabled and would bounce the user
              before this page can switch workspaces (SOX-97513). */}
          <Route
            path="/audit/optro/launch/:token"
            element={
              <RequireAuth>
                <OptroLaunchPage />
              </RequireAuth>
            }
          />
          <Route path="/" element={<Navigate to="/audit" replace />} />

          <Route path="*" element={<Navigate to="/audit" replace />} />
        </Route>,
      ),
    ),
  );

  return <RouterProvider router={router} />;
}

export default App;
